import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Receipt, Plus, Pencil, Trash2, CheckCircle2, XCircle, ShieldCheck, Wallet,
  AlertTriangle, Info, ExternalLink, ArrowRight, Lock, ClipboardList,
} from "lucide-react";
import { api, idr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import { useDebounce } from "@/hooks/useDebounce";
import LoadingState from "@/components/ui/loading-state";
import InputRupiah from "@/components/InputRupiah";
import { DatePicker, MonthPicker } from "@/components/ui/date-picker";
import { formatTanggal, hariIniISO } from "@/lib/tanggal";
import { useCabangOptions } from "@/lib/cabang";
import {
  PageHeader, TidakPunyaAkses, SearchInput, StatusBadge, SortTh, useSortState, sortRows,
  usePaginasi, Paginasi, unduhCsv, TombolEksporCsv, Modal, Field, inputCls, selectCls, textareaCls,
  InputCabang, useAkunKasBank, useCampaignMap, namaCampaign, usePinHapus, PinHapusDialog, adaField,
  DP_STATUS_META, PELUNASAN_STATUS_META, PLACEMENT_STATUS_META,
} from "./_shared";

// ============ Enum & label (mirror backend/routers/marketing/expense.py) ============
const CATEGORIES = ["kol", "ads_topup", "pfp", "production", "tools", "other"];
const CATEGORY_LABEL = {
  kol: "KOL (DP/Pelunasan/Barter)", ads_topup: "Top Up Saldo Iklan", pfp: "PFP / Paid Promote",
  production: "Produksi Konten", tools: "Tools / Software", other: "Lainnya",
};
const KOL_PAYMENT_TYPES = ["dp", "pelunasan", "barter"];
const KOL_PAYMENT_TYPE_LABEL = { dp: "DP", pelunasan: "Pelunasan", barter: "Barter Unit" };

const EXPENSE_STATUS_META = {
  draft: { label: "Draft", chip: "bg-slate-100 text-slate-600" },
  checked: { label: "Sudah Dicek", chip: "bg-sky-100 text-sky-700" },
  approved: { label: "Disetujui", chip: "bg-emerald-100 text-emerald-800" },
  paid: { label: "Dibayar", chip: "bg-emerald-600 text-white" },
  rejected: { label: "Ditolak", chip: "bg-rose-100 text-rose-700" },
};

// ============ Tiga status "Management" (brief owner Update 2) ============
// Nilainya DIHITUNG DI BACKEND (expense.py: approval turunan `status`, payment
// turunan uang yang tercatat, report tersimpan) — halaman ini cuma memberi
// label. Jangan pernah menghitung ulang salah satunya di sini: begitu ada dua
// perhitungan, keduanya akan berpisah jalan tanpa satu pun peringatan.
const APPROVAL_STATUS_META = {
  pending: { label: "Menunggu", chip: "bg-amber-100 text-amber-800" },
  approved: { label: "Disetujui", chip: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Ditolak", chip: "bg-rose-100 text-rose-700" },
};
const PAYMENT_STATUS_META = {
  pending: { label: "Belum Dibayar", chip: "bg-slate-100 text-slate-600" },
  partial: { label: "Sebagian", chip: "bg-amber-100 text-amber-800" },
  paid: { label: "Dibayar", chip: "bg-emerald-100 text-emerald-800" },
};
const REPORT_STATUS_META = {
  waiting: { label: "Menunggu Laporan", chip: "bg-slate-100 text-slate-600" },
  submitted: { label: "Diserahkan", chip: "bg-sky-100 text-sky-700" },
  reviewed: { label: "Ditinjau", chip: "bg-indigo-100 text-indigo-700" },
  completed: { label: "Selesai", chip: "bg-emerald-600 text-white" },
};
// Cermin PERSIS `REPORT_TRANSISI` di backend — dipakai supaya tombol yang
// pasti ditolak server tidak pernah ditampilkan (rule #3 CLAUDE.md).
const REPORT_TRANSISI = {
  waiting: ["submitted"],
  submitted: ["reviewed", "waiting"],
  reviewed: ["completed", "submitted"],
  completed: [],
};
const statusLaporan = (row) => (REPORT_STATUS_META[row?.report_status] ? row.report_status : "waiting");

// Bucket alokasi budget — SAMA persis `EXPENSE_CATEGORY_TO_BUCKET` di backend
// budget.py/expense.py. Duplikasi kecil disengaja (halaman ini berdiri sendiri
// dari MarketingBudget.jsx), pola yang sama dipakai backend sendiri.
const BUCKET_MAP = { kol: "kol", ads_topup: "ads", pfp: "pfp", production: "production", tools: "other", other: "other" };
const BUCKET_LABEL = { kol: "KOL / Endorsement", ads: "Iklan Digital (Ads)", pfp: "PFP / Paid Promote", production: "Produksi Konten", other: "Lainnya" };

/** Reject boleh SPV+ — mirror `bisa_approve_marketing()` backend (_shared.py). */
function bisaApproveMarketing(user, can, modul) {
  const role = (user?.role || "").toLowerCase();
  if (role === "admin" || role === "superadmin") return true;
  if (role.includes("spv")) return true;
  return can(modul, "update");
}

/** CEO (finance_ceo) + admin/superadmin — mirror `budget._bisa_approve_budget()`,
 *  dipakai backend untuk mengunci approval expense yang melebihi anggaran. */
function isCeoIsh(user) {
  const role = (user?.role || "").toLowerCase();
  return role === "finance_ceo" || role === "admin" || role === "superadmin";
}

/** Nominal EFEKTIF expense — untuk barter, nilainya ada di `hpp` (di-gate
 *  `marketing_margin`, field-nya HILANG SAMA SEKALI kalau tidak berizin —
 *  bukan bernilai null). `null` di sini berarti "tidak diketahui dari sini",
 *  BUKAN "nol" — jangan pernah menghitung ulang dari data lain. */
function efektifNominal(exp) {
  if (exp.category === "kol" && exp.kol_payment_type === "barter") {
    return adaField(exp, "hpp") ? Number(exp.hpp || 0) : null;
  }
  return Number(exp.amount || 0);
}
function nominalDisplay(exp) {
  const n = efektifNominal(exp);
  return n == null ? "-" : idr(n);
}

const emptyForm = () => ({
  tanggal: hariIniISO(), brand: "", category: "", campaign_id: "", payee_ref: "",
  amount: 0, payment_method: "", proof_url: "", catatan: "",
  kol_deal_id: "", kol_payment_type: "", imei_text: "",
  pfp_placement_id: "",
});

export default function MarketingExpense() {
  const { user, can } = useAuth();
  const bolehLihat = can("marketing_expense", "view");
  const bolehTambah = can("marketing_expense", "create");
  const bolehEdit = can("marketing_expense", "update");
  const bolehHapus = can("marketing_expense", "delete");
  const bolehLihatCampaign = can("marketing_campaign", "view");
  const bolehLihatKol = can("marketing_kol", "view");
  const bolehLihatPfp = can("marketing_pfp", "view");
  const bolehLihatBudget = can("marketing_budget", "view");
  // Pencarian unit IMEI untuk barter ada di modul Inventory, BUKAN Marketing —
  // gerbang izin-nya harus modul aslinya (rule #1 CLAUDE.md: jangan panggil
  // endpoint yang ujungnya 403). Tanpa izin ini user tetap bisa ketik manual.
  const bolehCariUnit = can("inventory_unit", "view");
  // Tautan "Lihat Jurnal" mendarat di /finance/jurnal/:txn, yang digerbangi
  // `finance_laporan.view` (kunci SAMA dengan require_permission di
  // buku_besar.py). Backend sudah membuang `journal_id` untuk siapa pun yang
  // bukan finance_*/admin (_strip_journal di expense.py), tapi role finance
  // yang izin laporannya dicabut tetap memegang journal_id — tanpa cek ini
  // tautannya muncul lalu berujung 403.
  const bolehLihatJurnal = can("finance_laporan", "view");
  const brandOpsi = useCabangOptions({ denganKosong: true });
  const { campaignMap } = useCampaignMap(bolehLihatCampaign);
  const akunKasBank = useAkunKasBank();

  const [q, setQ] = useState("");
  const qDebounced = useDebounce(q, 350);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [periodeFilter, setPeriodeFilter] = useState("");
  // Tiga filter Management. DIKIRIM KE SERVER, bukan disaring di layar —
  // daftar dipotong 5000 baris di backend, jadi menyaring di sini akan membuat
  // baris ke-5001 "tidak ada hasil" padahal ada (CLAUDE.md).
  const [approvalFilter, setApprovalFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [reportFilter, setReportFilter] = useState("");
  const { sortKey, sortDir, toggle } = useSortState("created_at", "desc");
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [bayarState, setBayarState] = useState(null);
  const [laporanTarget, setLaporanTarget] = useState(null);
  const { dialog: pinDialog, setDialog: setPinDialog, hapusDenganPin, submitDialog } = usePinHapus();

  const { data: dealsData } = useDataQuery(
    ["marketing", "kol-deals-untuk-expense"],
    () => api.get("/marketing/kol/deals").then((r) => r.data),
    { enabled: bolehLihatKol },
  );
  const deals = dealsData || [];
  const { data: placementsData } = useDataQuery(
    ["marketing", "pfp-placements-untuk-expense"],
    () => api.get("/marketing/pfp/placements").then((r) => r.data),
    { enabled: bolehLihatPfp },
  );
  const placements = placementsData || [];

  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "expense", statusFilter, categoryFilter, brandFilter, periodeFilter,
      approvalFilter, paymentFilter, reportFilter],
    () => api.get("/marketing/expense", {
      params: {
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(categoryFilter ? { category: categoryFilter } : {}),
        ...(brandFilter ? { brand: brandFilter } : {}),
        ...(periodeFilter ? { period: periodeFilter } : {}),
        ...(approvalFilter ? { approval_status: approvalFilter } : {}),
        ...(paymentFilter ? { payment_status: paymentFilter } : {}),
        ...(reportFilter ? { report_status: reportFilter } : {}),
      },
    }).then((r) => r.data.items),
    { enabled: bolehLihat },
  );

  const filtered = useMemo(() => {
    let out = data || [];
    const t = qDebounced.trim().toLowerCase();
    if (t) out = out.filter((e) => e.code?.toLowerCase().includes(t) || e.payee_ref?.toLowerCase().includes(t));
    return out;
  }, [data, qDebounced]);
  const sorted = sortRows(filtered, sortKey, sortDir);
  const { page, setPage, totalPages, awal, akhir } = usePaginasi(sorted.length, 15);
  const halaman = sorted.slice(awal, akhir);

  const reload = () => refetch();

  const eksporCsv = () => {
    unduhCsv(`expense-marketing-${hariIniISO()}.csv`, [
      { label: "Kode", value: (r) => r.code },
      { label: "Tanggal", value: (r) => r.tanggal },
      { label: "Brand", value: (r) => r.brand },
      { label: "Kategori", value: (r) => CATEGORY_LABEL[r.category] || r.category },
      { label: "Jenis KOL", value: (r) => r.kol_payment_type ? KOL_PAYMENT_TYPE_LABEL[r.kol_payment_type] : "" },
      { label: "Nominal", value: (r) => (efektifNominal(r) ?? "") },
      { label: "Status", value: (r) => EXPENSE_STATUS_META[r.status]?.label || r.status },
      { label: "Approval", value: (r) => APPROVAL_STATUS_META[r.approval_status]?.label || r.approval_status || "" },
      { label: "Payment", value: (r) => PAYMENT_STATUS_META[r.payment_status]?.label || r.payment_status || "" },
      { label: "Report", value: (r) => REPORT_STATUS_META[statusLaporan(r)].label },
      { label: "Dibuat Oleh", value: (r) => r.created_by_name || r.created_by || "" },
    ], sorted);
  };

  const kategoriTersedia = CATEGORIES.filter((c) => {
    if (c === "kol") return bolehLihatKol;
    if (c === "pfp") return bolehLihatPfp;
    return true;
  });

  const bukaTambah = () => { setEditId(null); setForm(emptyForm()); };
  const bukaEdit = (exp) => {
    setEditId(exp.id);
    setForm({
      tanggal: exp.tanggal, brand: exp.brand, category: exp.category, campaign_id: exp.campaign_id || "",
      payee_ref: exp.payee_ref || "", amount: exp.amount || 0, payment_method: exp.payment_method || "",
      proof_url: exp.proof_url || "", catatan: exp.catatan || "",
      kol_deal_id: exp.kol_deal_id || "", kol_payment_type: exp.kol_payment_type || "",
      imei_text: (exp.imei_list || []).join("\n"),
      pfp_placement_id: exp.pfp_placement_id || "",
    });
  };
  const ubahKategori = (cat) => setForm((f) => ({
    ...f, category: cat, kol_deal_id: "", kol_payment_type: "", imei_text: "", pfp_placement_id: "", amount: 0,
  }));

  const simpan = async () => {
    if (!form.tanggal) { toast.error("Tanggal wajib diisi"); return; }
    if (!form.brand.trim()) { toast.error("Brand wajib diisi"); return; }
    if (!form.category) { toast.error("Kategori wajib dipilih"); return; }
    const body = {
      tanggal: form.tanggal, campaign_id: form.campaign_id || null, brand: form.brand.trim(),
      category: form.category, payee_ref: form.payee_ref, amount: 0,
      payment_method: form.payment_method, proof_url: form.proof_url, catatan: form.catatan,
      kol_deal_id: null, kol_payment_type: null, imei_list: [], pfp_placement_id: null,
    };
    if (form.category === "kol") {
      if (!form.kol_deal_id) { toast.error("Pilih Deal KOL dulu"); return; }
      if (!form.kol_payment_type) { toast.error("Pilih jenis pembayaran KOL dulu"); return; }
      body.kol_deal_id = form.kol_deal_id;
      body.kol_payment_type = form.kol_payment_type;
      if (form.kol_payment_type === "barter") {
        const list = [...new Set((form.imei_text || "").split(/\n|,/).map((s) => s.trim()).filter(Boolean))];
        if (list.length === 0) { toast.error("Pilih/ketik minimal 1 IMEI/SN untuk barter"); return; }
        body.imei_list = list;
      }
    } else if (form.category === "pfp") {
      if (!form.pfp_placement_id) { toast.error("Pilih Placement PFP dulu"); return; }
      body.pfp_placement_id = form.pfp_placement_id;
    } else {
      if (!form.amount || form.amount <= 0) { toast.error("Nominal wajib lebih dari 0"); return; }
      body.amount = Number(form.amount);
    }
    setBusy(true);
    try {
      if (editId) {
        const r = await api.patch(`/marketing/expense/${editId}`, body);
        toast.success(r.data.message || "Expense diperbarui");
      } else {
        const r = await api.post("/marketing/expense", body);
        toast.success(r.data.message || "Expense dibuat (draft)");
      }
      setForm(null); setEditId(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan expense"); }
    finally { setBusy(false); }
  };

  const cekExpense = async (exp) => {
    if (!window.confirm(`Tandai expense ${exp.code} sudah dicek?`)) return;
    try {
      const r = await api.post(`/marketing/expense/${exp.id}/check`);
      toast.success(r.data.message || "Expense lolos pengecekan");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencek expense"); }
  };

  const tolak = async (exp) => {
    const alasan = window.prompt(`Alasan menolak expense ${exp.code}?`) || "";
    if (!alasan.trim()) return;
    try {
      const r = await api.post(`/marketing/expense/${exp.id}/reject`, { alasan });
      toast.success(r.data.message || "Expense ditolak");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menolak expense"); }
  };

  const bayarDelegasi = async (exp) => {
    const tujuan = exp.category === "pfp" ? "PFP > Placement" : "KOL > Deal";
    if (!window.confirm(`Tautkan pembayaran ${exp.code}? Pastikan pembayarannya SUDAH dilakukan di menu ${tujuan} terlebih dahulu.`)) return;
    try {
      const r = await api.post(`/marketing/expense/${exp.id}/pay`, {});
      toast.success(r.data.message || "Pembayaran tertaut");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menautkan pembayaran"); }
  };

  /** Tombol Laporan hanya muncul kalau memang ADA perpindahan yang sah dari
   *  status sekarang, dan expense-nya belum ditolak — dua kondisi yang sama
   *  persis dipakai backend untuk menolak (jangan tampilkan tombol yang 403/400). */
  const bisaUbahLaporan = (exp) =>
    bolehEdit && exp.status !== "rejected" && (REPORT_TRANSISI[statusLaporan(exp)] || []).length > 0;

  const hapus = async (exp) => {
    if (!window.confirm(`Hapus expense ${exp.code}?`)) return;
    const aksi = (pin) => api.delete(`/marketing/expense/${exp.id}`, pin ? { params: { pin } } : undefined);
    const langsung = await hapusDenganPin(aksi, { modul: "marketing_expense", targetId: exp.id, ringkasan: `Hapus expense ${exp.code}` });
    if (langsung) { toast.success("Expense dihapus"); reload(); }
  };

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={Receipt} title="Expense & Jurnal" />
        <TidakPunyaAkses label="Expense Marketing" testid="mkt-exp-tanpa-akses" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-expense">
      <PageHeader
        icon={Receipt} title="Expense & Jurnal"
        desc="Ajukan dan proses pengeluaran marketing lewat alur draft → dicek → disetujui → dibayar."
        actions={<>
          <TombolEksporCsv onClick={eksporCsv} testid="mkt-exp-export" />
          {bolehTambah && (
            <button onClick={bukaTambah} data-testid="mkt-exp-tambah"
                    className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Ajukan Expense
            </button>
          )}
        </>}
      />

      <div className="bg-sky-50 border border-sky-200 rounded-xl p-3.5 text-xs text-sky-800 space-y-1" data-testid="mkt-exp-info-3tingkat">
        <p className="font-semibold inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Alur 3 tingkat: Draft → Dicek → Disetujui → Dibayar</p>
        <p>
          Pembuat, pemeriksa, dan penyetuju WAJIB tiga orang berbeda — sistem otomatis menolak kalau ada yang sama
          (tombol "Cek"/"Setujui" akan dikunci dengan penjelasan kalau itu penyebabnya). Kategori KOL (DP/Pelunasan)
          dan PFP TIDAK dibayar dari sini — pembayarannya lewat menu KOL/PFP, di sini hanya ditautkan. Persetujuan
          yang melebihi anggaran brand hanya bisa dilakukan CEO.
        </p>
      </div>

      {/* Toolbar filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <SearchInput value={q} onChange={setQ} placeholder="Cari kode/penerima..." testid="mkt-exp-cari" className="w-56" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="mkt-exp-filter-status" className={`${selectCls} h-9 w-auto`}>
          <option value="">Semua Status</option>
          {Object.entries(EXPENSE_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="mkt-exp-filter-kategori" className={`${selectCls} h-9 w-auto`}>
          <option value="">Semua Kategori</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
        </select>
        <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} data-testid="mkt-exp-filter-brand" className={`${selectCls} h-9 w-auto`}>
          <option value="">Semua Brand</option>
          {brandOpsi.filter(Boolean).map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <MonthPicker value={periodeFilter} onChange={setPeriodeFilter} placeholder="Semua periode" data-testid="mkt-exp-filter-periode" className="w-36" />
        <select value={approvalFilter} onChange={(e) => setApprovalFilter(e.target.value)} data-testid="mkt-exp-filter-approval" className={`${selectCls} h-9 w-auto`}>
          <option value="">Semua Approval</option>
          {Object.entries(APPROVAL_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} data-testid="mkt-exp-filter-payment" className={`${selectCls} h-9 w-auto`}>
          <option value="">Semua Payment</option>
          {Object.entries(PAYMENT_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={reportFilter} onChange={(e) => setReportFilter(e.target.value)} data-testid="mkt-exp-filter-report" className={`${selectCls} h-9 w-auto`}>
          <option value="">Semua Report</option>
          {Object.entries(REPORT_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <SortTh label="Kode / Tanggal" sortKeyName="tanggal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-exp-sort-tanggal" />
                <SortTh label="Brand" sortKeyName="brand" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-exp-sort-brand" />
                <th className="px-3 py-2.5 font-semibold">Kategori</th>
                <th className="px-3 py-2.5 font-semibold text-right">Nominal</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5 font-semibold">Approval</th>
                <th className="px-3 py-2.5 font-semibold">Payment</th>
                <th className="px-3 py-2.5 font-semibold">Report</th>
                <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <LoadingState loading={loading} error={error} onRetry={refetch} kosong={!loading && !error && halaman.length === 0}
                            varian="tabel" kolom={9} pesanKosong="Belum ada expense yang diajukan." data-testid="mkt-exp-state">
                {halaman.map((exp) => {
                  const isMaker = user?.email && user.email === exp.created_by;
                  const isMakerOrChecker = user?.email && [exp.created_by, exp.checked_by].includes(user.email);
                  const bolehSetujui = bisaApproveMarketing(user, can, "marketing_expense") || isCeoIsh(user);
                  const bolehTolak = bisaApproveMarketing(user, can, "marketing_expense");
                  return (
                    <tr key={exp.id} className="hover:bg-slate-50/60 align-top" data-testid={`mkt-exp-row-${exp.id}`}>
                      <td className="px-3 py-2.5">
                        <p className="font-mono text-[11px] text-slate-500">{exp.code}</p>
                        <p className="text-slate-700">{formatTanggal(exp.tanggal)}</p>
                        {exp.payee_ref && <p className="text-slate-400 text-[11px]">{exp.payee_ref}</p>}
                        {bolehLihatCampaign && exp.campaign_id && <p className="text-slate-400 text-[11px]">{namaCampaign(campaignMap, exp.campaign_id)}</p>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">{exp.brand}</td>
                      <td className="px-3 py-2.5">
                        <p className="text-slate-700">{CATEGORY_LABEL[exp.category] || exp.category}</p>
                        {exp.kol_payment_type && <p className="text-[11px] text-slate-400">{KOL_PAYMENT_TYPE_LABEL[exp.kol_payment_type]}</p>}
                        {exp.delegated && <p className="text-[10px] text-sky-600 inline-flex items-center gap-0.5"><Info className="w-3 h-3" /> Tertaut (bayar di menu lain)</p>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">{nominalDisplay(exp)}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-col items-start gap-1">
                          <StatusBadge meta={EXPENSE_STATUS_META} value={exp.status} />
                          {adaField(exp, "journal_id") && exp.journal_id && bolehLihatJurnal && (
                            <Link to={`/finance/jurnal/${exp.journal_id}`} data-testid={`mkt-exp-jurnal-${exp.id}`}
                                  title="Buka detail jurnal transaksi ini (debit, kredit, dokumen sumber)"
                                  className="text-[10px] font-mono text-emerald-700 hover:underline inline-flex items-center gap-0.5">
                              Jurnal {exp.journal_id.slice(0, 8)}… <ExternalLink className="w-2.5 h-2.5" />
                            </Link>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5" data-testid={`mkt-exp-approval-${exp.id}`}>
                        <StatusBadge meta={APPROVAL_STATUS_META} value={exp.approval_status} />
                      </td>
                      <td className="px-3 py-2.5" data-testid={`mkt-exp-payment-${exp.id}`}>
                        <div className="flex flex-col items-start gap-1" title={exp.payment_status_catatan || undefined}>
                          <StatusBadge meta={PAYMENT_STATUS_META} value={exp.payment_status} />
                          {exp.payment_status_catatan && (
                            <span className="text-[10px] text-amber-700 inline-flex items-center gap-0.5">
                              <AlertTriangle className="w-2.5 h-2.5 shrink-0" /> belum ditautkan
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5" data-testid={`mkt-exp-report-${exp.id}`}>
                        <div className="flex flex-col items-start gap-1">
                          <StatusBadge meta={REPORT_STATUS_META} value={statusLaporan(exp)} />
                          {exp.report_status_by_name && (
                            <span className="text-[10px] text-slate-400">
                              {exp.report_status_by_name}{exp.report_status_at ? ` · ${formatTanggal(exp.report_status_at.slice(0, 10))}` : ""}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          {exp.status === "draft" && bolehEdit && (
                            isMaker ? (
                              <span title="Anda pembuat expense ini — pemeriksa harus orang lain (kontrol 3-tingkat)."
                                    className="px-2 h-7 rounded bg-slate-50 text-slate-400 text-[11px] font-medium inline-flex items-center gap-1 cursor-not-allowed">
                                <Lock className="w-3 h-3" /> Cek
                              </span>
                            ) : (
                              <button onClick={() => cekExpense(exp)} data-testid={`mkt-exp-cek-${exp.id}`}
                                      className="px-2 h-7 rounded bg-sky-50 hover:bg-sky-100 text-sky-700 text-[11px] font-medium inline-flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Cek
                              </button>
                            )
                          )}
                          {exp.status === "checked" && bolehSetujui && (
                            isMakerOrChecker ? (
                              <span title="Anda pembuat/pemeriksa expense ini — penyetuju harus orang ketiga (kontrol 3-tingkat)."
                                    className="px-2 h-7 rounded bg-slate-50 text-slate-400 text-[11px] font-medium inline-flex items-center gap-1 cursor-not-allowed">
                                <Lock className="w-3 h-3" /> Setujui
                              </span>
                            ) : (
                              <button onClick={() => setApproveTarget(exp)} data-testid={`mkt-exp-setujui-${exp.id}`}
                                      className="px-2 h-7 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11px] font-medium inline-flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Setujui
                              </button>
                            )
                          )}
                          {["draft", "checked", "approved"].includes(exp.status) && bolehTolak && (
                            <button onClick={() => tolak(exp)} data-testid={`mkt-exp-tolak-${exp.id}`}
                                    className="px-2 h-7 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 text-[11px] font-medium inline-flex items-center gap-1">
                              <XCircle className="w-3 h-3" /> Tolak
                            </button>
                          )}
                          {exp.status === "approved" && bolehEdit && (
                            exp.delegated ? (
                              <button onClick={() => bayarDelegasi(exp)} data-testid={`mkt-exp-tautkan-${exp.id}`}
                                      title={exp.delegasi_info}
                                      className="px-2 h-7 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11px] font-medium inline-flex items-center gap-1">
                                <ArrowRight className="w-3 h-3" /> Tautkan Pembayaran
                              </button>
                            ) : (
                              <button onClick={() => setBayarState({ exp, tipe: exp.category === "kol" ? "barter" : "cash", sumber_akun_id: "", tanggal: exp.tanggal || hariIniISO() })}
                                      data-testid={`mkt-exp-bayar-${exp.id}`}
                                      className="px-2 h-7 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11px] font-medium inline-flex items-center gap-1">
                                <Wallet className="w-3 h-3" /> Bayar
                              </button>
                            )
                          )}
                          {bisaUbahLaporan(exp) && (
                            <button onClick={() => setLaporanTarget(exp)} data-testid={`mkt-exp-laporan-${exp.id}`}
                                    title="Ubah status laporan pertanggungjawaban beban ini"
                                    className="px-2 h-7 rounded bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-[11px] font-medium inline-flex items-center gap-1">
                              <ClipboardList className="w-3 h-3" /> Laporan
                            </button>
                          )}
                          {exp.status === "draft" && bolehEdit && (
                            <button onClick={() => bukaEdit(exp)} data-testid={`mkt-exp-edit-${exp.id}`}
                                    className="w-7 h-7 rounded hover:bg-slate-100 text-slate-500 grid place-items-center">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {["draft", "rejected"].includes(exp.status) && bolehHapus && (
                            <button onClick={() => hapus(exp)} data-testid={`mkt-exp-hapus-${exp.id}`}
                                    className="w-7 h-7 rounded hover:bg-rose-50 text-rose-600 grid place-items-center">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </LoadingState>
            </tbody>
          </table>
        </div>
        <Paginasi page={page} setPage={setPage} totalPages={totalPages} testid="mkt-exp-paginasi" />
      </div>

      {/* Form ajukan/edit expense */}
      <Modal open={!!form} onClose={() => { setForm(null); setEditId(null); }} title={editId ? "Edit Expense" : "Ajukan Expense Baru"}
             maxWidth="max-w-xl" testid="mkt-exp-modal"
             footer={<>
               <button onClick={() => { setForm(null); setEditId(null); }} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={simpan} disabled={busy} data-testid="mkt-exp-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                 {busy ? "Menyimpan..." : editId ? "Simpan" : "Ajukan"}
               </button>
             </>}>
        {form && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tanggal" required>
                <DatePicker value={form.tanggal} onChange={(v) => setForm((f) => ({ ...f, tanggal: v }))} data-testid="mkt-exp-f-tanggal" />
              </Field>
              <Field label="Brand" required>
                <InputCabang value={form.brand} onChange={(v) => setForm((f) => ({ ...f, brand: v }))} opsi={brandOpsi} testid="mkt-exp-f-brand" />
              </Field>
            </div>
            {bolehLihatCampaign && (
              <Field label="Campaign (opsional)">
                <select value={form.campaign_id} onChange={(e) => setForm((f) => ({ ...f, campaign_id: e.target.value }))} data-testid="mkt-exp-f-campaign" className={selectCls}>
                  <option value="">— tidak terkait campaign —</option>
                  {Object.values(campaignMap).map((c) => <option key={c.id} value={c.id}>{c.code} — {c.nama}</option>)}
                </select>
              </Field>
            )}
            <Field label="Kategori" required>
              <select value={form.category} onChange={(e) => ubahKategori(e.target.value)} disabled={!!editId} data-testid="mkt-exp-f-kategori" className={selectCls}>
                <option value="">— pilih kategori —</option>
                {CATEGORIES.map((c) => {
                  const nonaktif = !kategoriTersedia.includes(c);
                  return <option key={c} value={c} disabled={nonaktif}>{CATEGORY_LABEL[c]}{nonaktif ? " (butuh akses modul terkait)" : ""}</option>;
                })}
              </select>
            </Field>

            {form.category === "kol" && (
              <>
                <Field label="Deal KOL" required>
                  <select value={form.kol_deal_id} onChange={(e) => setForm((f) => ({ ...f, kol_deal_id: e.target.value }))} data-testid="mkt-exp-f-kol-deal" className={selectCls}>
                    <option value="">— pilih deal —</option>
                    {deals.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.code} — {d.kol_nama} (DP: {DP_STATUS_META[d.dp_status]?.label || d.dp_status}, Pelunasan: {PELUNASAN_STATUS_META[d.pelunasan_status]?.label || d.pelunasan_status})
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Jenis Pembayaran" required>
                  <select value={form.kol_payment_type}
                          onChange={(e) => setForm((f) => ({ ...f, kol_payment_type: e.target.value, imei_text: e.target.value === "barter" ? f.imei_text : "" }))}
                          data-testid="mkt-exp-f-kol-tipe" className={selectCls}>
                    <option value="">— pilih jenis —</option>
                    {KOL_PAYMENT_TYPES.map((t) => <option key={t} value={t}>{KOL_PAYMENT_TYPE_LABEL[t]}</option>)}
                  </select>
                </Field>

                {(form.kol_payment_type === "dp" || form.kol_payment_type === "pelunasan") && (
                  <div className="text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-lg p-3 space-y-1.5">
                    <p className="font-semibold inline-flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Pembayaran lewat menu KOL, bukan di sini</p>
                    <p>
                      DP/Pelunasan KOL dibayar dari menu <b>KOL &gt; Deal</b> (tombol "Bayar DP"/"Bayar Pelunasan"). Expense
                      ini HANYA menautkan pembayaran itu untuk pencatatan brand &amp; kontrol anggaran — tidak membuat jurnal
                      baru dan tidak memicu pembayaran baru. Pastikan status pembayarannya sudah "Dibayar" di sana sebelum
                      menandai expense ini dibayar.
                    </p>
                    <Link to="/marketing/kol" className="inline-flex items-center gap-1 font-medium hover:underline">Buka menu KOL <ExternalLink className="w-3 h-3" /></Link>
                  </div>
                )}
                {form.kol_payment_type === "barter" && (
                  <BarterUnitPicker imeiText={form.imei_text} setImeiText={(v) => setForm((f) => ({ ...f, imei_text: v }))} bolehCari={bolehCariUnit} />
                )}
              </>
            )}

            {form.category === "pfp" && (
              <>
                <Field label="Placement PFP" required>
                  <select value={form.pfp_placement_id} onChange={(e) => setForm((f) => ({ ...f, pfp_placement_id: e.target.value }))} data-testid="mkt-exp-f-pfp-placement" className={selectCls}>
                    <option value="">— pilih placement —</option>
                    {placements.map((p) => (
                      <option key={p.id} value={p.id}>{p.code} — {p.pfp_nama_akun} ({PLACEMENT_STATUS_META[p.status]?.label || p.status})</option>
                    ))}
                  </select>
                </Field>
                <div className="text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-lg p-3 space-y-1.5">
                  <p className="font-semibold inline-flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Pembayaran lewat menu PFP, bukan di sini</p>
                  <p>
                    Placement PFP dibayar dari menu <b>PFP &gt; Placement</b> (tombol "Bayar"). Expense ini HANYA
                    menautkan pembayaran itu untuk pencatatan brand &amp; kontrol anggaran — tidak membuat jurnal baru.
                    Pastikan placement sudah berstatus "Selesai (Dibayar)" sebelum menandai expense ini dibayar.
                  </p>
                  <Link to="/marketing/pfp" className="inline-flex items-center gap-1 font-medium hover:underline">Buka menu PFP <ExternalLink className="w-3 h-3" /></Link>
                </div>
              </>
            )}

            {["ads_topup", "production", "tools", "other"].includes(form.category) && (
              <Field label="Nominal (Rp)" required>
                <InputRupiah value={form.amount} onChange={(v) => setForm((f) => ({ ...f, amount: v }))} testid="mkt-exp-f-amount" className={inputCls} />
              </Field>
            )}

            <Field label="Penerima / Vendor">
              <input value={form.payee_ref} onChange={(e) => setForm((f) => ({ ...f, payee_ref: e.target.value }))} data-testid="mkt-exp-f-payee" className={inputCls} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Metode Pembayaran" hint="Informasi saja — akun sumber dana dipilih saat tombol Bayar ditekan.">
                <input value={form.payment_method} onChange={(e) => setForm((f) => ({ ...f, payment_method: e.target.value }))} data-testid="mkt-exp-f-metode" className={inputCls} placeholder="cth. Transfer BCA" />
              </Field>
              <Field label="Bukti (URL)">
                <input value={form.proof_url} onChange={(e) => setForm((f) => ({ ...f, proof_url: e.target.value }))} data-testid="mkt-exp-f-bukti" className={inputCls} placeholder="Link invoice/kuitansi" />
              </Field>
            </div>
            <Field label="Catatan">
              <textarea value={form.catatan} rows={2} onChange={(e) => setForm((f) => ({ ...f, catatan: e.target.value }))} data-testid="mkt-exp-f-catatan" className={textareaCls} />
            </Field>
          </>
        )}
      </Modal>

      {approveTarget && (
        <ApproveModal exp={approveTarget} onClose={() => setApproveTarget(null)}
                      onSuccess={() => { setApproveTarget(null); reload(); }}
                      bolehLihatBudget={bolehLihatBudget} user={user} />
      )}
      {bayarState && (
        <BayarModal state={bayarState} setState={setBayarState} akunKasBank={akunKasBank}
                    onSuccess={reload} />
      )}
      {laporanTarget && (
        <LaporanModal exp={laporanTarget} user={user} onClose={() => setLaporanTarget(null)}
                      onSuccess={() => { setLaporanTarget(null); reload(); }} />
      )}

      <PinHapusDialog dialog={pinDialog} setDialog={setPinDialog} onSubmit={() => submitDialog(reload)} />
    </div>
  );
}

// ============ Pemilih unit barter — cari (kalau berizin inventory_unit) + ketik manual ============
function BarterUnitPicker({ imeiText, setImeiText, bolehCari }) {
  const [cari, setCari] = useState("");
  const cariDebounced = useDebounce(cari, 350);
  const { data, loading } = useDataQuery(
    ["inventory", "units-cari-barter", cariDebounced],
    () => api.get("/inventory/units", { params: { status: "tersedia", q: cariDebounced, limit: 10 } }).then((r) => r.data.items),
    { enabled: bolehCari && cariDebounced.trim().length >= 3 },
  );
  const daftar = (imeiText || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const tambah = (identifier) => {
    if (daftar.includes(identifier)) { toast.info("Unit ini sudah ada di daftar"); return; }
    setImeiText(daftar.length ? `${imeiText}\n${identifier}` : identifier);
    setCari("");
  };

  return (
    <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50/60">
      <p className="text-xs font-semibold text-slate-700">Unit yang dibarter ke KOL</p>
      {bolehCari ? (
        <div className="relative">
          <SearchInput value={cari} onChange={setCari} placeholder="Cari IMEI/SN unit tersedia..." testid="mkt-exp-f-barter-cari" />
          {cariDebounced.trim().length >= 3 && (
            <div className="border border-slate-200 rounded-lg mt-1 max-h-40 overflow-y-auto bg-white shadow-sm relative z-10">
              {loading && <p className="p-2 text-xs text-slate-400">Mencari...</p>}
              {!loading && (data || []).length === 0 && <p className="p-2 text-xs text-slate-400">Tidak ada unit tersedia yang cocok.</p>}
              {(data || []).map((u) => (
                <button type="button" key={u.id} onClick={() => tambah(u.identifier)} data-testid={`mkt-exp-f-barter-pilih-${u.id}`}
                        className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-emerald-50 flex items-center justify-between gap-2">
                  <span className="font-mono">{u.identifier}</span>
                  <span className="text-slate-400 truncate">{u.nama}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="text-[11px] text-slate-500">Anda tidak punya akses pencarian Inventory — ketik/scan manual IMEI/SN di bawah.</p>
      )}
      <Field label={`Daftar IMEI/SN (${daftar.length})`} hint="Satu per baris.">
        <textarea value={imeiText} rows={4} data-testid="mkt-exp-f-barter-list"
                  onChange={(e) => setImeiText(e.target.value)} className={textareaCls} placeholder={"358240051111110\nSN-ABC123"} />
      </Field>
    </div>
  );
}

// ============ Modal status laporan (sumbu Report) ============
// Pilihan yang ditawarkan DITURUNKAN dari `REPORT_TRANSISI` yang mencerminkan
// backend — bukan daftar keempat status. Menawarkan lompatan yang pasti ditolak
// server sama saja dengan menampilkan tombol yang berujung galat.
function LaporanModal({ exp, user, onClose, onSuccess }) {
  const sekarang = statusLaporan(exp);
  const opsi = REPORT_TRANSISI[sekarang] || [];
  // Peninjau tidak boleh orang yang menyerahkan — aturan yang SAMA ditegakkan
  // backend; di sini opsinya dinonaktifkan supaya tidak berujung 400.
  const akuPenyerah = !!user?.email && user.email === exp.report_submitted_by;
  const terkunci = (tujuan) => tujuan === "reviewed" && akuPenyerah;
  const opsiPertamaAktif = opsi.find((o) => !terkunci(o)) || "";
  const [status, setStatus] = useState(opsiPertamaAktif);
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!status) { toast.error("Pilih status laporan tujuan"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/marketing/expense/${exp.id}/report`, { status, catatan });
      toast.success(r.data.message || "Status laporan diperbarui");
      onSuccess();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status laporan"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Status Laporan Beban" maxWidth="max-w-md" testid="mkt-exp-laporan-modal"
           footer={<>
             <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
             <button onClick={submit} disabled={busy || !status} data-testid="mkt-exp-laporan-submit"
                     className="h-10 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50">
               {busy ? "Memproses..." : "Simpan"}
             </button>
           </>}>
      <p className="text-sm text-slate-600">
        <b>{exp.code}</b> — {CATEGORY_LABEL[exp.category] || exp.category}<br />
        Status laporan sekarang: <StatusBadge meta={REPORT_STATUS_META} value={sekarang} />
      </p>
      {exp.report_status_by_name && (
        <p className="text-xs text-slate-500">
          Terakhir diubah {exp.report_status_by_name}
          {exp.report_status_at ? ` pada ${formatTanggal(exp.report_status_at.slice(0, 10))}` : ""}
          {exp.report_catatan ? ` — "${exp.report_catatan}"` : ""}
        </p>
      )}
      <Field label="Pindahkan ke" required>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="mkt-exp-laporan-status" className={selectCls}>
          <option value="">— pilih status —</option>
          {opsi.map((o) => (
            <option key={o} value={o} disabled={terkunci(o)}>
              {REPORT_STATUS_META[o].label}{terkunci(o) ? " (Anda yang menyerahkan — peninjau harus orang lain)" : ""}
            </option>
          ))}
        </select>
      </Field>
      {akuPenyerah && opsi.includes("reviewed") && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 inline-flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Anda yang menyerahkan laporan ini, jadi peninjauannya harus dilakukan orang lain (pemisahan tugas,
          sama seperti maker/checker/approver di alur bebannya). Anda tetap bisa menariknya kembali.
        </p>
      )}
      <Field label="Catatan" hint="Tersimpan di riwayat laporan beserta nama dan waktunya.">
        <textarea value={catatan} rows={2} onChange={(e) => setCatatan(e.target.value)} data-testid="mkt-exp-laporan-catatan" className={textareaCls} />
      </Field>
      {(exp.report_riwayat || []).length > 0 && (
        <div className="text-[11px] text-slate-500 border border-slate-200 rounded-lg p-2.5 space-y-1 max-h-32 overflow-y-auto" data-testid="mkt-exp-laporan-riwayat">
          <p className="font-semibold text-slate-600">Riwayat</p>
          {(exp.report_riwayat || []).map((r, i) => (
            <p key={i}>
              {REPORT_STATUS_META[r.dari]?.label || r.dari} → {REPORT_STATUS_META[r.ke]?.label || r.ke}
              {" · "}{r.by_name || r.by}{r.at ? ` · ${formatTanggal(r.at.slice(0, 10))}` : ""}
              {r.catatan ? ` — ${r.catatan}` : ""}
            </p>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ============ Modal approve — proyeksi dampak budget SEBELUM klik Setujui ============
function ApproveModal({ exp, onClose, onSuccess, bolehLihatBudget, user }) {
  const [busy, setBusy] = useState(false);
  const period = (exp.tanggal || "").slice(0, 7);
  const { data: burn, loading } = useDataQuery(
    ["marketing", "expense-approve-burn", exp.brand, period],
    () => api.get("/marketing/budget/burn-rate", { params: { brand: exp.brand, period } }).then((r) => r.data),
    { enabled: bolehLihatBudget },
  );
  const nominal = efektifNominal(exp);
  const bucket = BUCKET_MAP[exp.category];
  const bucketRow = burn?.per_bucket?.find((b) => b.bucket === bucket);
  const projectedBucket = nominal != null && bucketRow ? bucketRow.actual + nominal : null;
  const overBucket = projectedBucket != null && bucketRow?.alokasi > 0 && projectedBucket > bucketRow.alokasi + 0.01;
  const projectedTotal = nominal != null && burn ? burn.grand_total + nominal : null;
  const overTotal = projectedTotal != null && burn?.amount_total > 0 && projectedTotal > burn.amount_total + 0.01;
  const over = overBucket || overTotal;
  const ceoIsh = isCeoIsh(user);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/marketing/expense/${exp.id}/approve`);
      toast.success(r.data.message || "Expense disetujui");
      onSuccess();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyetujui expense"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Setujui Expense" maxWidth="max-w-md" testid="mkt-exp-approve-modal"
           footer={<>
             <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
             <button onClick={submit} disabled={busy || (over && !ceoIsh)} data-testid="mkt-exp-approve-submit"
                     title={over && !ceoIsh ? "Melebihi anggaran — hanya CEO yang bisa menyetujui" : undefined}
                     className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
               {busy ? "Memproses..." : "Setujui"}
             </button>
           </>}>
      <p className="text-sm text-slate-600">
        <b>{exp.code}</b> — {CATEGORY_LABEL[exp.category]}{exp.kol_payment_type ? ` (${KOL_PAYMENT_TYPE_LABEL[exp.kol_payment_type]})` : ""}<br />
        {exp.brand} · {formatTanggal(exp.tanggal)} · <span className="font-mono">{nominalDisplay(exp)}</span>
      </p>

      {!bolehLihatBudget && (
        <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5">
          Anda tidak punya akses melihat Budget Control, jadi dampak anggaran tidak bisa diperkirakan di sini. Kalau
          ternyata melebihi anggaran, sistem akan menolak persetujuan dan meminta approval CEO.
        </p>
      )}

      {bolehLihatBudget && (
        <LoadingState loading={loading} varian="kartu" jumlahKartu={1} data-testid="mkt-exp-approve-burn-loading">
          {burn && nominal == null && (
            <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5">
              Nilai barter ini tersembunyi dari izin Anda — dampak ke anggaran tidak bisa dihitung di sini, tapi tetap
              diperhitungkan sistem saat disetujui.
            </p>
          )}
          {burn && nominal != null && (
            <div className={`rounded-lg border p-3 text-xs space-y-1 ${over ? "border-rose-300 bg-rose-50" : "border-emerald-200 bg-emerald-50/50"}`} data-testid="mkt-exp-approve-proyeksi">
              <p className="font-semibold text-slate-700">Proyeksi setelah disetujui ({BUCKET_LABEL[bucket] || bucket}):</p>
              {bucketRow && bucketRow.alokasi > 0 && (
                <p>Bucket: {idr(projectedBucket)} / {idr(bucketRow.alokasi)} {overBucket && <b className="text-rose-700">— TERLAMPAUI</b>}</p>
              )}
              {burn.amount_total > 0 && (
                <p>Total brand: {idr(projectedTotal)} / {idr(burn.amount_total)} {overTotal && <b className="text-rose-700">— TERLAMPAUI</b>}</p>
              )}
              {!(bucketRow?.alokasi > 0) && !(burn.amount_total > 0) && <p className="text-slate-500">Belum ada anggaran disetujui untuk brand/periode ini.</p>}
            </div>
          )}
        </LoadingState>
      )}

      {over && (
        <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5 inline-flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Menyetujui expense ini akan MELEBIHI anggaran brand ini — hanya CEO yang bisa melanjutkan persetujuan ini
          {!ceoIsh && ", jadi tombol Setujui dinonaktifkan untuk Anda"}.
        </p>
      )}
    </Modal>
  );
}

// ============ Modal bayar — akun kas/bank untuk kategori tunai, konfirmasi unit untuk barter ============
// Sumber KAS KECIL (category "petty_cash") didelegasikan backend ke mesin kas
// kecil Finance (`petty_cash.py::petty_expense`, lihat expense.py) — foto nota
// WAJIB dan saldo dicek di sana. Peringatan di sini murni UX (mencegah klik
// "Bayar" yang sudah pasti akan 400 dari backend), penegakan tetap di server.
function BayarModal({ state, setState, akunKasBank, onSuccess }) {
  const [busy, setBusy] = useState(false);
  const { exp, tipe } = state;
  const akunTerpilih = akunKasBank.find((a) => a.id === state.sumber_akun_id);
  const sumberKasKecil = tipe === "cash" && akunTerpilih?.category === "petty_cash";
  const nominal = efektifNominal(exp);
  const notaKosong = !exp.proof_url;
  const saldoKurang = sumberKasKecil && nominal != null && Number(akunTerpilih.balance || 0) < nominal;
  // K3 (brief 4 Sep 2026): kas kecil yang jenisnya belum disetel atau cabangnya
  // belum berkode TIDAK bisa menerbitkan nomor bukti, dan `petty_expense`
  // menolaknya 400. Dinonaktifkan di sini supaya tombol Bayar tidak pernah
  // ditampilkan untuk pilihan yang pasti gagal (CLAUDE.md #3). Medannya aditif:
  // `undefined` (respons lama) tidak pernah dianggap bermasalah.
  const kodeBelumSiap = sumberKasKecil && akunTerpilih.kode_bukti_siap === false;
  const kasKecilBermasalah = sumberKasKecil && (notaKosong || saldoKurang || kodeBelumSiap);

  const submit = async () => {
    if (tipe === "cash" && !state.sumber_akun_id) { toast.error("Pilih akun sumber pembayaran"); return; }
    if (kasKecilBermasalah) {
      toast.error(kodeBelumSiap
        ? akunTerpilih.kode_bukti_masalah
        : notaKosong
          ? "Kas kecil wajib foto nota — lengkapi kolom \"Bukti (URL)\" di form expense dulu"
          : "Saldo kas kecil tidak cukup untuk nominal ini");
      return;
    }
    setBusy(true);
    try {
      const body = { tanggal: state.tanggal || hariIniISO() };
      if (tipe === "cash") body.sumber_akun_id = state.sumber_akun_id;
      const r = await api.post(`/marketing/expense/${exp.id}/pay`, body);
      toast.success(r.data.message || "Expense dibayar");
      setState(null);
      onSuccess();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membayar expense"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={() => setState(null)} title={tipe === "barter" ? "Bayar Barter Unit ke KOL" : "Bayar Expense"} maxWidth="max-w-sm" testid="mkt-exp-bayar-modal"
           footer={<>
             <button onClick={() => setState(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
             <button onClick={submit} disabled={busy || kasKecilBermasalah} data-testid="mkt-exp-bayar-submit"
                     title={kasKecilBermasalah ? (kodeBelumSiap ? akunTerpilih.kode_bukti_masalah : notaKosong ? "Foto nota belum ada" : "Saldo kas kecil tidak cukup") : undefined}
                     className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
               {busy ? "Memproses..." : "Bayar"}
             </button>
           </>}>
      <p className="text-sm text-slate-600">
        {exp.code} — {CATEGORY_LABEL[exp.category]}
        {tipe === "cash" && <><br /><span className="font-mono font-semibold">{nominalDisplay(exp)}</span></>}
      </p>
      {tipe === "barter" && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
          Membayar akan MENGONSUMSI {(exp.imei_list || []).length} unit berikut sebagai barter (statusnya berubah,
          tidak bisa lagi dijual/ditransfer): <span className="font-mono">{(exp.imei_list || []).join(", ")}</span>.
          Nilai HPP final dihitung ulang saat ini (bukan angka prakiraan draft).
        </div>
      )}
      {tipe === "cash" && (
        <Field label="Dari Akun" required hint="Pilih akun kas kecil untuk memakai dana kas kecil Marketing sebagai sumber.">
          <select value={state.sumber_akun_id} onChange={(e) => setState((s) => ({ ...s, sumber_akun_id: e.target.value }))} data-testid="mkt-exp-bayar-akun" className={selectCls}>
            <option value="">Pilih akun...</option>
            {akunKasBank.map((a) => (
              <option key={a.id} value={a.id} disabled={a.category === "petty_cash" && a.kode_bukti_siap === false}>
                {a.code} — {a.name}{a.category === "petty_cash" ? ` · Kas Kecil (saldo ${idr(a.balance)})` : ""}
                {a.category === "petty_cash" && a.kode_bukti_siap === false ? " — belum siap (nomor bukti)" : ""}
              </option>
            ))}
          </select>
        </Field>
      )}
      {sumberKasKecil && (
        <div className={`text-xs rounded-lg border p-2.5 space-y-1 ${kasKecilBermasalah ? "border-rose-300 bg-rose-50 text-rose-700" : "border-sky-200 bg-sky-50 text-sky-800"}`}
             data-testid="mkt-exp-bayar-petty-info">
          <p className="font-semibold inline-flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Sumber Kas Kecil</p>
          <p>Saldo saat ini: <b>{idr(akunTerpilih.balance)}</b>. Kas kecil WAJIB foto nota dan saldo harus cukup —
             berlaku aturan kas kecil Finance yang sama.</p>
          {notaKosong && (
            <p className="font-medium inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              Expense ini belum ada foto nota (kolom "Bukti (URL)" di form) — lengkapi dulu sebelum bisa dibayar dari kas kecil.
            </p>
          )}
          {saldoKurang && (
            <p className="font-medium inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              Saldo kas kecil tidak cukup untuk nominal {idr(nominal)}.
            </p>
          )}
          {kodeBelumSiap && (
            <p className="font-medium inline-flex items-center gap-1" data-testid="mkt-exp-bayar-kode-masalah">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {akunTerpilih.kode_bukti_masalah}
            </p>
          )}
        </div>
      )}
      <Field label="Tanggal">
        <DatePicker value={state.tanggal} onChange={(v) => setState((s) => ({ ...s, tanggal: v }))} data-testid="mkt-exp-bayar-tanggal" />
      </Field>
    </Modal>
  );
}
